import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { Lang, LANGS, LOCALE, translate } from './index';

/**
 * Language state, shared by the live2 board and the analysis views.
 *
 * The choice persists in localStorage under one key, so switching language on
 * one page carries to the other. Each page mounts its own provider (they are
 * separate routes) but they read and write the same key.
 */

const STORAGE_KEY = 'live2.lang';

function readStored(): Lang {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && (LANGS as string[]).includes(v)) return v as Lang;
  } catch {
    // Private browsing or blocked storage — fall through to the default.
  }
  return 'de';
}

type I18nValue = {
  lang: Lang;
  locale: string;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nValue>({
  lang: 'de',
  locale: LOCALE.de,
  setLang: () => undefined,
  t: (key) => translate('de', key),
});

export const useI18n = () => useContext(I18nContext);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(readStored);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // Not fatal — the choice just will not survive a reload.
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      locale: LOCALE[lang],
      setLang,
      t: (key, vars) => translate(lang, key, vars),
    }),
    [lang, setLang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/**
 * Three-way language toggle.
 *
 * `dark` matches the analysis views' navy chrome; the default suits the light
 * live2 board. Deliberately not placed in Topbar, which is shared with the old
 * /live board that has no translations.
 */
export const LangSwitch: React.FC<{ sx?: object; dark?: boolean }> = ({ sx, dark }) => {
  const { lang, setLang } = useI18n();
  const idle = dark ? 'rgba(255,255,255,0.55)' : '#607d8b';
  const idleBg = dark ? 'transparent' : '#ffffff';
  const activeBg = dark ? '#f0a500' : '#18364a';
  const activeFg = dark ? '#0d1b2a' : '#ffffff';
  const border = dark ? 'rgba(255,255,255,0.2)' : '#cfd8dc';

  return (
    <Box sx={{
      display: 'inline-flex', border: `1px solid ${border}`,
      borderRadius: '4px', overflow: 'hidden', ...sx,
    }}>
      {LANGS.map((l) => (
        <Box
          key={l}
          component="button"
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          sx={{
            border: 'none',
            cursor: 'pointer',
            px: '8px',
            py: '2px',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.06em',
            fontFamily: dark ? '"IBM Plex Mono", monospace' : 'inherit',
            backgroundColor: lang === l ? activeBg : idleBg,
            color: lang === l ? activeFg : idle,
            '&:hover': { color: lang === l ? activeFg : (dark ? '#f0a500' : '#18364a') },
          }}
        >
          {l.toUpperCase()}
        </Box>
      ))}
    </Box>
  );
};
